import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse, AxiosError } from "axios";
import Problem from "../types/problem";
import { UserDTO } from "../types/userDTO";
import apiClient from "../main/apiClient";

const useFetchUsers = (organisationId: string) => {
    return useQuery<UserDTO[], AxiosError>({
        queryKey: ["user"],
        queryFn: () =>
          apiClient.get(`/organisation/${organisationId}/user`).then((resp) => resp.data),
    });
}

const useFetchUser = (organisationId: string, userId: string) => {
  return useQuery<UserDTO, AxiosError>({
    queryKey: ["user", userId],
    queryFn: () =>
      apiClient.get(`/organisation/${organisationId}/user/${userId}`).then((resp) => resp.data),
    refetchOnMount: "always"
  });
};

const useUpdateUser = (organisationId: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, UserDTO>({
    mutationFn: (userDTO) => apiClient.put(`/organisation/${organisationId}/user`, userDTO),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    }
  });
};

const useAddUser = (organisationId: string, setUserState: (user: UserDTO) => void) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, UserDTO>({
    mutationFn: (userDTO) => apiClient.post(`/organisation/${organisationId}/user`, userDTO),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error, variables) => {
      console.log(error);
      setUserState(variables);
    }
  });
};

const useDeleteUser = (organisationId: string) => {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError<Problem>, UserDTO>({
    mutationFn: (userDTO) => apiClient.delete(`/organisation/${organisationId}/user`, { data: userDTO }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    }
  });
};

/**
 * Hook for resending welcome message to user
 *
 * @param organisationId - The organisation ID of the user to resend the welcome message to
 * @param userId - The user ID of the user to resend the welcome message to
 * @returns
 */
const useResendWelcomeMessage = (organisationId: string, userId: string) => {
  return useMutation<AxiosResponse, AxiosError<Problem>, UserDTO>({
    mutationFn: (userDTO) => apiClient.post(`/organisation/${organisationId}/user/${userId}/resend-welcome-message`, userDTO),
  });
}

export { useFetchUsers, useFetchUser, useAddUser, useUpdateUser, useDeleteUser, useResendWelcomeMessage }
